import React, { useLayoutEffect, useRef } from "react";
import styled from "styled-components/macro";
import { SelectionHighlightData } from "../../models/selectionHightlightData";

const SelCanvas = styled.canvas`
    @keyframes flash {
        60% {
            opacity: 0.3;
            animation-timing-function: ease-out;
        }
        100% {
            opacity: 0;
            animation-timing-function: ease-in;
        }
    }

    position: absolute;
    cursor: pointer;
    opacity: 0;
    z-index: 1;
    will-change: opacity, width, height, top, left;
    pointer-events: none;
    &.animate {
        animation: flash 0.8s 1;
    }
`;

export const SelectionCanvas = ({ highlightData }: { highlightData: SelectionHighlightData | undefined }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useLayoutEffect(() => {
        if (!canvasRef.current || !highlightData) return;

        const { maskData, configImageRect, maskWidth, maskHeight, selectedValues } = highlightData;

        // Create a small canvas for drawing an alpha mask
        const scaleCanvas = document.createElement("canvas");
        const scaleContext = scaleCanvas.getContext("2d");
        if (!scaleContext) return;
        scaleCanvas.style.position = "absolute";
        scaleCanvas.style.top = "0";
        scaleCanvas.style.left = "0";
        scaleCanvas.width = maskWidth;
        scaleCanvas.height = maskHeight;
        const scaleImageData = scaleContext.getImageData(0, 0, maskWidth, maskHeight);
        const scaleData = scaleImageData.data;

        const imageRatioX = configImageRect.width / maskWidth;
        const imageRatioY = configImageRect.height / maskHeight;
        // Fill the alpha mask for the selected color

        let minX = maskWidth;
        let maxX = 0;
        let minY = maskHeight;
        let maxY = 0;

        /* eslint-disable no-bitwise */
        const numValues = selectedValues.length;
        if (numValues === 1) {
            const [singleValue] = selectedValues;
            for (let y = 0, ymw = 0; y < maskHeight; y += 1, ymw += maskWidth) {
                for (let x = 0, p = ymw; x < maskWidth; x += 1, p += 1) {
                    if (maskData[p] === singleValue) {
                        const ip = (p << 2) + 3;
                        scaleData[ip] = 255;
                        minX = x < minX ? x : minX;
                        maxX = x > maxX ? x : maxX;
                        minY = y < minY ? y : minY;
                        maxY = y > maxY ? y : maxY;
                    }
                }
            }
        } else {
            for (let y = 0, ymw = 0; y < maskHeight; y += 1, ymw += maskWidth) {
                for (let x = 0, p = ymw; x < maskWidth; x += 1, p += 1) {
                    for (let i = 0; i < numValues; i += 1) {
                        if (maskData[p] === selectedValues[i]) {
                            const ip = (p << 2) + 3;
                            scaleData[ip] = 255;
                            minX = x < minX ? x : minX;
                            maxX = x > maxX ? x : maxX;
                            minY = y < minY ? y : minY;
                            maxY = y > maxY ? y : maxY;
                        }
                    }
                }
            }
        }
        scaleContext.putImageData(scaleImageData, 0, 0);

        // Don't show selection if it has no width or height (should never occur)
        const smallWidth = maxX - minX + 1;
        const smallHeight = maxY - minY + 1;
        const selWidth = (smallWidth * imageRatioX) | 0;
        const selHeight = (smallHeight * imageRatioY) | 0;
        /* eslint-enable no-bitwise */

        if (selWidth > 0 && selHeight > 0) {
            // Replace the old selection canvas with a new one
            canvasRef.current.id = "selectionCanvas";
            canvasRef.current.width = selWidth;
            canvasRef.current.height = selHeight;
            canvasRef.current.style.left = `${minX * imageRatioX + configImageRect.x}px`;
            canvasRef.current.style.top = `${minY * imageRatioY + configImageRect.y}px`;
            const selectionContext = canvasRef.current.getContext("2d");

            // Draw the small alpha mask to the large selection image
            if (!selectionContext) return;

            selectionContext.globalCompositeOperation = "source-over";
            selectionContext.drawImage(scaleCanvas, minX, minY, smallWidth, smallHeight, 0, 0, selWidth, selHeight);

            // Fill the selection with a pattern using the alpha mask
            selectionContext.globalCompositeOperation = "source-in";
            selectionContext.fillStyle = "white";
            selectionContext.fillRect(0, 0, selWidth, selHeight);

            canvasRef.current.classList.remove("animate");
            // eslint-disable-next-line
            void canvasRef.current.offsetHeight; // trigger a reflow
            canvasRef.current.classList.add("animate");
        }
    }, [highlightData]);

    return <SelCanvas ref={canvasRef} />;
};
